import React, { useCallback, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog";
import { diffObject, isBlank, notBlank } from "../../util/ext";
import { useConfirm } from "../../common/use-confirm";
import { lensPath, set } from "ramda";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import { useProjectCreate } from "../cspServices";
import {
  validateProjectDetails,
  validateProjectDetailsField,
} from "../cspProjectValidation";

const NewProjectDialog = ({ open, program, onCancel, onApply }) => {
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const defaultProject = useMemo(
    () => ({
      projectName: null,
      projectKey: null,
      programId: program,
      collectPurchaseLink: false,
      useDefaultCategories: false,
      termsUri: "https://terms.librariesshare.com/terms.html",
      redirectUri:
        "https://ProgramName.librariesshare.com/ProjectKey/thank-you",
      audiences: [],
      categories: [],
      optIns: [],
      submissionTypes: [],
      projectManagerEmails: [],
      subProjects: [],
      fieldOptions: {},
      libraryOptions: [],
      emailTemplate: "default-submission-notification.ftl",
      emailSubject: "Thank you for your submission!",
      landingPageTemplate: "landing-page",
      thankYouPageTemplate: "thank-you",
      socialLinks: [],
      defaultPublicationDateFormat: null,
    }),
    [program],
  );

  const [project, setProject] = useState(defaultProject);

  const createProject = useProjectCreate();
  const handleCreate = useCallback(async () => {
    const validationErrors = validateProjectDetails(project);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      try {
        const result = await createProject.mutateAsync({
          data: project,
        });
        onApply(result);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [createProject, project, onApply, setValidationErrors, setGeneralError]);

  const handleClose = useCallback(() => {
    if (isBlank(diffObject(defaultProject, project))) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have made changes to this program without saving.  Do you wish to discard these changes and go back to the program list?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [project, defaultProject, showConfirm, onCancel]);

  const handleBlur = (field, value) => {
    const errors = validateProjectDetailsField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = notBlank(event.target) ? event.target.value : event;
    setProject(prev => set(lensPath(path), value, prev));
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="new-project-dialog-title">
        Enter Project Details
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <TextField
              id="project-name"
              fullWidth
              label="Name"
              value={project.projectName || ""}
              onChange={handleTextChange(["projectName"])}
              error={hasValidationError("projectName")}
              helperText={firstValidationError("projectName")}
              onBlur={() => handleBlur("projectName", project.projectName)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="project-key"
              fullWidth
              label="Key"
              value={project.projectKey || ""}
              onChange={handleTextChange(["projectKey"])}
              error={hasValidationError("projectKey")}
              helperText={firstValidationError("projectKey")}
              onBlur={() => handleBlur("projectKey", project.projectKey)}
            />
          </Grid>
        </Grid>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleCreate} color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewProjectDialog;
